import { createBrowserClient } from '@supabase/ssr'
import { createClient as createSupabaseClient } from '@supabase/supabase-js'

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''

let _client: ReturnType<typeof createBrowserClient> | null = null

export function createClient() {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    console.warn(
      '⚠️ Supabase env vars are missing. Create a .env.local file with NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY.'
    )
    // Return a proxy that won't crash but does nothing.
    // This lets the app render during local dev before Supabase is configured.
    return new Proxy({} as ReturnType<typeof createBrowserClient>, {
      get: (_target, prop) => {
        if (prop === 'from') return () => new Proxy({} as any, {
          get: () => () => Promise.resolve({ data: [], error: { message: 'Supabase not configured' } })
        })
        if (prop === 'auth') return new Proxy({} as any, {
          get: () => () => Promise.resolve({ data: { user: null, session: null }, error: null })
        })
        return () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } })
      }
    })
  }

  if (!_client) {
    _client = createBrowserClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  }
  return _client
}

let _recoveryClient: ReturnType<typeof createSupabaseClient> | null = null

/**
 * A SECOND client, used by exactly one page (`app/auth/forgot`) for exactly one
 * call: `resetPasswordForEmail`.
 *
 * Why it cannot be the client above: `createBrowserClient` hardcodes
 * `flowType: "pkce"` — it is set AFTER the caller's `options.auth` is spread,
 * so asking for `implicit` there is silently ignored. And auth-js only sends a
 * `code_challenge` to `/recover` when the flow IS pkce (see
 * `GoTrueClient.resetPasswordForEmail`). GoTrue then stores the one-time token
 * with a `pkce_` prefix, and that prefixed hash cannot be redeemed without the
 * verifier sitting in THIS browser's storage — so the mail is dead on a phone.
 *
 * Implicit flow sends no challenge, so the token is stored unprefixed and
 * `/auth/reset` can redeem it with `verifyOtp` anywhere.
 *
 * It never persists anything: `resetPasswordForEmail` needs no session, and a
 * second client writing to the app's storage key would fight the real one over
 * whoever is logged in at the till. Hence `persistSession: false` plus a
 * storage key of its own.
 */
export function createRecoveryClient() {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    return createClient() as unknown as ReturnType<typeof createSupabaseClient>
  }
  if (!_recoveryClient) {
    _recoveryClient = createSupabaseClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        flowType: 'implicit',
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storageKey: 'sb-accessoryshop-recovery',
      },
    })
  }
  return _recoveryClient
}
